import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckSquare, WarningCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Requests bell — approval queue (leave / delete / edit requests requiring admin action).
 * Shows a red badge for admin (things they need to approve).
 * For non-admin, shows only their own pending count in a muted tone.
 */
export default function RequestsBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [count, setCount] = useState(0);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/requests/pending-count");
      setCount(r.data.pending || 0);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.debug("RequestsBell poll failed:", e?.message);
    }
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!user) return null;
  const isAdmin = user.role === "admin";
  const active = count > 0;
  const label = isAdmin ? "Approval queue" : "My requests";

  return (
    <button
      data-testid="bell-btn"
      onClick={() => nav("/requests")}
      title={label}
      className={`relative inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors ${
        active
          ? "bg-red-600/20 text-red-300 hover:bg-red-600/30"
          : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
      }`}
    >
      {active ? <WarningCircle size={16} weight="fill" /> : <CheckSquare size={16} weight="bold" />}
      {count > 0 && (
        <span
          data-testid="bell-count"
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full font-mono ring-2 ring-[color:var(--kr-nav-bg)]"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}
